import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { ApiError } from "@/lib/api";
import { QuestionForm } from "@/components/QuestionForm";
import {
  requestPriceIndication,
  getCarrierQuestions,
  requestCarrierQuote,
  missingFieldsOf,
  type BrokerQuote,
  type CarrierIndication,
  type CarrierQuestion,
  type MissingField,
  type OfferTermView,
  type QuoteAnswer,
} from "@/lib/brokerSubmissions";
import { messageFor } from "@/lib/errors";

// BRK-S049 → BRK-S053 — the multi-carrier comparison table (flow steps 4-6).
// One indication request FANS OUT server-side across every eligible carrier
// and returns per-carrier results: quoted carriers render as offer columns;
// referred / declined / errored carriers render as informative column states
// with their reasons (a declined market is information the broker wants, not
// an error to hide — one carrier's failure never blanks the table).
//
// - The indication is AUTO-PULLED once the submission is quote-ready (no
//   manual click); duplicate fires are guarded. Not-ready (400, incl. the
//   structured missing-fields checklist) and error states are handled (NN #7).
// - Row spine: a fixed financial core (premium/taxes/cost/term/plans) + the
//   product manifest's offer-terms display schema, RESOLVED onto each quote
//   (`offerTerms`, PMS-S143) — no product- or WC-specific term names here.
// - Each offer column is badged Indication vs Quote. An Indication column's
//   action runs the firm-quote flow; on success the carrier's column flips to
//   a Quote in place. A Quote column shows a Bind affordance, disabled until
//   the PMS bind endpoint lands.

function money(value: string): string {
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

// The quote-level financial core — product-agnostic outputs every carrier
// returns, rendered above the manifest-driven term groups.
const FINANCIAL_ROWS: { label: string; render: (q: BrokerQuote) => string }[] =
  [
    { label: "Total premium", render: (q) => money(q.totalPremium) },
    { label: "Taxes & fees", render: (q) => money(q.taxesAndFees) },
    { label: "Total cost", render: (q) => money(q.totalCost) },
    {
      label: "Policy term",
      render: (q) =>
        q.policyEffectiveDate
          ? `${q.policyEffectiveDate}${q.policyExpirationDate ? ` – ${q.policyExpirationDate}` : ""}`
          : "—",
    },
    { label: "Quote valid until", render: (q) => q.quoteValidUntil ?? "—" },
    {
      label: "Payment plans",
      render: (q) =>
        q.paymentPlans.length
          ? q.paymentPlans.map((p) => p.planType).join(", ")
          : "—",
    },
  ];

// Format a resolved offer-term value by its manifest field type.
function formatTermValue(term: OfferTermView): string {
  const v = term.value;
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (term.type === "currency") return money(String(v));
  return String(v);
}

function termFor(quote: BrokerQuote, key: string): OfferTermView | undefined {
  for (const group of quote.offerTerms) {
    const term = group.terms.find((t) => t.key === key);
    if (term) return term;
  }
  return undefined;
}

/** One comparison column: a carrier's offer (quote) OR its non-quoted fan-out
 *  outcome (declined / referred / errored, with reasons). `carrierId` (from
 *  the fan-out results — the persisted quote view doesn't carry it) addresses
 *  the carrier for the firm-quote step; without it the action is unavailable. */
interface OfferColumn {
  key: string;
  carrier: string | null;
  carrierId: string | null;
  quote: BrokerQuote | null;
  outcome: Exclude<CarrierIndication["outcome"], "quoted"> | null;
  reasons: string[];
}

// Collapse persisted quotes to one column per carrier (preferring a Quote over
// an Indication so a firmed-up carrier flips in place; newest wins within a
// kind), then append the fan-out's non-quoted carriers as outcome columns. A
// quoted leg whose persisted quote hasn't landed yet falls back to the inline
// result quote, so the table never flashes empty between pull and refresh.
function toColumns(
  quotes: BrokerQuote[],
  outcomes: CarrierIndication[] | null,
): OfferColumn[] {
  const byCarrier = new Map<string, BrokerQuote>();
  for (const q of quotes) {
    const key = q.carrier ?? q.id;
    const existing = byCarrier.get(key);
    if (
      !existing ||
      (existing.kind === "indication" && q.kind === "quote") ||
      (existing.kind === q.kind && q.createdAt > existing.createdAt)
    ) {
      byCarrier.set(key, q);
    }
  }
  const idByCarrier = new Map(
    (outcomes ?? []).map((r) => [r.carrier, r.carrierId]),
  );
  const columns: OfferColumn[] = [...byCarrier.entries()].map(([key, q]) => ({
    key,
    carrier: q.carrier,
    carrierId: q.carrier ? (idByCarrier.get(q.carrier) ?? null) : null,
    quote: q,
    outcome: null,
    reasons: [],
  }));
  for (const r of outcomes ?? []) {
    if (byCarrier.has(r.carrier)) continue;
    if (r.outcome === "quoted") {
      if (r.quote)
        columns.push({
          key: r.carrierId,
          carrier: r.carrier,
          carrierId: r.carrierId,
          quote: r.quote,
          outcome: null,
          reasons: [],
        });
      continue;
    }
    columns.push({
      key: r.carrierId,
      carrier: r.carrier,
      carrierId: r.carrierId,
      quote: null,
      outcome: r.outcome,
      reasons: r.reasons,
    });
  }
  return columns;
}

export function QuoteComparison({
  submissionId,
  quotes,
  onQuotesChanged,
  quoteFloorMissing = [],
  onCompleteProfile,
}: {
  submissionId: string;
  quotes: BrokerQuote[];
  onQuotesChanged: () => void | Promise<void>;
  /** The account-scope QUOTE-floor fields the insured doesn't yet satisfy
   *  (BRK-S057). When non-empty, a carrier's firm-quote action is GATED: it
   *  routes the broker to complete the profile first instead of firing a request
   *  that the server would reject. */
  quoteFloorMissing?: MissingField[];
  /** Open/scroll the page's insured-profile completion form. */
  onCompleteProfile?: () => void;
}) {
  const { authedRequest } = useAuth();

  const [pulling, setPulling] = useState(false);
  const [notReady, setNotReady] = useState<string | null>(null);
  // The structured missing-fields list off a quote-readiness 400 (PMS-S144) —
  // rendered as a field-level checklist instead of the raw message (BRK-S052).
  const [missingFields, setMissingFields] = useState<MissingField[] | null>(
    null,
  );
  const [pullError, setPullError] = useState<string | null>(null);
  // The per-carrier fan-out results (BRK-S053) — the non-quoted legs render as
  // informative column states alongside the persisted quote columns.
  const [outcomes, setOutcomes] = useState<CarrierIndication[] | null>(null);

  const columns = useMemo(
    () => toColumns(quotes, outcomes),
    [quotes, outcomes],
  );
  // The shared term-row spine: terms describe the insured's requested terms and
  // are the same across a submission's quotes, so any offer column carries it.
  const termGroups = columns.find((c) => c.quote)?.quote?.offerTerms ?? [];

  // Auto-pull the indication exactly once per submission, when quote-ready with
  // no offers yet. The fire is guarded by a per-submission ref (not the effect
  // cleanup), so a re-render from an unstable authedRequest / onQuotesChanged
  // never cancels the in-flight pull — reading the latest values off refs keeps
  // them out of the dependency array. A mounted ref avoids setState-after-unmount.
  const pulledFor = useRef<string | null>(null);
  const mounted = useRef(true);
  const quotesRef = useRef(quotes);
  quotesRef.current = quotes;
  const changedRef = useRef(onQuotesChanged);
  changedRef.current = onQuotesChanged;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (pulledFor.current === submissionId || quotesRef.current.length > 0)
      return;
    pulledFor.current = submissionId;
    (async () => {
      setPulling(true);
      setNotReady(null);
      setMissingFields(null);
      setPullError(null);
      setOutcomes(null);
      try {
        const res = await requestPriceIndication(authedRequest, submissionId);
        if (mounted.current) setOutcomes(res.results);
        await changedRef.current();
      } catch (err) {
        if (!mounted.current) return;
        if (err instanceof ApiError && err.status === 400) {
          setNotReady(err.message);
          setMissingFields(missingFieldsOf(err));
        } else setPullError(messageFor(err));
      } finally {
        if (mounted.current) setPulling(false);
      }
    })();
  }, [submissionId, authedRequest]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col">
        <h2 className="font-semibold text-brk-text">Pricing &amp; quotes</h2>
        <p className="text-sm text-brk-dim">
          Indicative pricing from our markets — get a firm quote to bind.
        </p>
      </div>

      {pulling && (
        <p role="status" className="text-sm text-brk-dim">
          Getting price indications…
        </p>
      )}
      {notReady && (
        <div
          role="status"
          className="rounded-md border border-brk-border bg-brk-bg px-3 py-2 text-sm text-brk-dim"
        >
          This submission needs a few more details before we can price it.
          {missingFields ? (
            <ul className="mt-1 list-inside list-disc">
              {missingFields.map((f) => (
                <li key={f.key}>
                  {f.label}
                  {f.scope === "account" && (
                    <span className="text-xs"> — on the insured profile</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            ` ${notReady}`
          )}
        </div>
      )}
      {pullError && (
        <p role="alert" className="text-sm text-brk-danger">
          {pullError}
        </p>
      )}
      {outcomes && outcomes.length === 0 && columns.length === 0 && (
        <div className="rounded-md border border-brk-border bg-brk-bg px-3 py-2 text-sm text-brk-dim">
          No markets are currently available for this product — our team will
          follow up on this submission.
        </div>
      )}

      {columns.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-brk-border">
          <table className="w-full min-w-[32rem] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-brk-border">
                <th className="p-3 font-medium text-brk-dim">Offer terms</th>
                {columns.map((col) =>
                  col.quote ? (
                    <OfferColumnHeader
                      key={col.key}
                      quote={col.quote}
                      carrierId={col.carrierId}
                      submissionId={submissionId}
                      onQuotesChanged={onQuotesChanged}
                      quoteFloorMissing={quoteFloorMissing}
                      onCompleteProfile={onCompleteProfile}
                    />
                  ) : (
                    <OutcomeColumnHeader key={col.key} column={col} />
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              <SectionRow title="Financials" span={columns.length + 1} />
              {FINANCIAL_ROWS.map((row) => (
                <tr key={row.label} className="border-t border-brk-border">
                  <th
                    scope="row"
                    className="whitespace-nowrap px-3 py-2 font-normal text-brk-dim"
                  >
                    {row.label}
                  </th>
                  {columns.map((col) => (
                    <td key={col.key} className="px-3 py-2 text-brk-text">
                      {col.quote ? row.render(col.quote) : "—"}
                    </td>
                  ))}
                </tr>
              ))}
              {termGroups.map((group) => (
                <Fragment key={group.label}>
                  <SectionRow title={group.label} span={columns.length + 1} />
                  {group.terms.map((term) => (
                    <tr key={term.key} className="border-t border-brk-border">
                      <th
                        scope="row"
                        className="whitespace-nowrap px-3 py-2 font-normal text-brk-dim"
                      >
                        {term.label}
                      </th>
                      {columns.map((col) => {
                        const t = col.quote
                          ? termFor(col.quote, term.key)
                          : undefined;
                        return (
                          <td key={col.key} className="px-3 py-2 text-brk-text">
                            {t ? formatTermValue(t) : "—"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SectionRow({ title, span }: { title: string; span: number }) {
  return (
    <tr className="bg-brk-bg/60">
      <th
        colSpan={span}
        className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-brk-dim"
      >
        {title}
      </th>
    </tr>
  );
}

// A non-quoted carrier's column: a labeled state (Declined / Referred /
// Unavailable) + the carrier's reasons — a declined market is information the
// broker wants, presented alongside the live offers.
function OutcomeColumnHeader({ column }: { column: OfferColumn }) {
  const label =
    column.outcome === "declined"
      ? "Declined"
      : column.outcome === "referred"
        ? "Referred"
        : "Unavailable";
  return (
    <th className="min-w-[12rem] p-3 align-top">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="rounded-full border border-brk-border bg-brk-bg px-2 py-0.5 text-xs font-medium text-brk-dim">
            {label}
          </span>
          <span className="font-semibold text-brk-text">{column.carrier}</span>
        </div>
        {column.reasons.length > 0 && (
          <ul className="list-inside list-disc text-xs font-normal text-brk-dim">
            {column.reasons.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        )}
      </div>
    </th>
  );
}

// One offer column's header: a kind badge + carrier, and the in-cell action —
// "Get a firm quote" on an indication (loads THAT carrier's normalized
// questions inline via the carrier-addressed routes and flips the column to a
// Quote on success — other carriers' columns are untouched), or a disabled
// "Bind" on a quote. The action needs the fan-out's carrierId; a column
// rendered from a persisted quote alone (no fan-out this session) has no
// carrier address, so the action is unavailable until pricing is re-run.
function OfferColumnHeader({
  quote,
  carrierId,
  submissionId,
  onQuotesChanged,
  quoteFloorMissing,
  onCompleteProfile,
}: {
  quote: BrokerQuote;
  carrierId: string | null;
  submissionId: string;
  onQuotesChanged: () => void | Promise<void>;
  quoteFloorMissing: MissingField[];
  onCompleteProfile?: () => void;
}) {
  const { authedRequest } = useAuth();
  const [questions, setQuestions] = useState<CarrierQuestion[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [notReady, setNotReady] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A structured quote-readiness 400 from the firm-quote request (PMS-S144),
  // rendered as a per-field checklist that routes back to the profile — never a
  // raw string (BRK-S057).
  const [quoteMissing, setQuoteMissing] = useState<MissingField[] | null>(null);

  // The quote-stage account floor isn't met — the request would 400. Gate it.
  const floorBlocked = quoteFloorMissing.length > 0;

  // The one place the firm-quote request is issued (both the zero-question fast
  // path and the answered-questions path). It OWNS its errors so neither the
  // QuestionForm's raw-string fallback nor onStart's handler double-renders: a
  // structured 400 → the checklist; other failures → a handled message. It
  // never rethrows, so QuestionForm settles cleanly and stays open for a retry.
  async function submitAnswers(answers: QuoteAnswer[]) {
    if (!carrierId) return;
    setQuoteMissing(null);
    setNotReady(null);
    setError(null);
    try {
      await requestCarrierQuote(
        authedRequest,
        submissionId,
        carrierId,
        answers,
      );
      setQuestions(null);
      await onQuotesChanged();
    } catch (err) {
      const missing = missingFieldsOf(err);
      if (missing) setQuoteMissing(missing);
      else if (err instanceof ApiError && err.status === 400)
        setNotReady(err.message);
      else setError(messageFor(err));
    }
  }

  async function onStart() {
    if (!carrierId) return;
    setLoading(true);
    setError(null);
    setNotReady(null);
    setQuoteMissing(null);
    try {
      const res = await getCarrierQuestions(
        authedRequest,
        submissionId,
        carrierId,
      );
      if (res.questions.length === 0) {
        // Everything is already known from the field bag — go straight to the
        // quote request, no empty form flash (BRK-S054).
        await submitAnswers([]);
        return;
      }
      setQuestions(res.questions);
    } catch (err) {
      const missing = missingFieldsOf(err);
      if (missing) setQuoteMissing(missing);
      else if (err instanceof ApiError && err.status === 400)
        setNotReady(err.message);
      else setError(messageFor(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <th className="min-w-[12rem] p-3 align-top">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <KindBadge kind={quote.kind} />
          <span className="font-semibold text-brk-text">
            {quote.carrier ?? "Carrier"}
          </span>
        </div>
        {quote.carrierQuoteNumber && (
          <span className="font-mono text-xs font-normal text-brk-dim">
            {quote.carrierQuoteNumber}
          </span>
        )}

        {quote.kind === "quote" ? (
          <button
            type="button"
            disabled
            title="Binding is coming soon"
            className="cursor-not-allowed self-start rounded-md border border-brk-border px-3 py-1.5 text-sm font-medium text-brk-dim opacity-70"
          >
            Bind <span className="font-normal">(coming soon)</span>
          </button>
        ) : floorBlocked && carrierId ? (
          // Gated: complete the insured profile before a firm quote can be
          // requested (BRK-S057). The request stays unavailable until the floor
          // is met — a route into the profile form, not a dead button.
          <ProfileGate
            missing={quoteFloorMissing}
            onCompleteProfile={onCompleteProfile}
          />
        ) : questions === null && carrierId ? (
          <button
            type="button"
            onClick={onStart}
            disabled={loading}
            className="self-start rounded-md bg-brk-accent px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:opacity-60"
          >
            {loading ? "Loading…" : "Get a firm quote"}
          </button>
        ) : null}

        {notReady && (
          <span role="status" className="text-xs font-normal text-brk-dim">
            Needs a few more details before we can quote it. {notReady}
          </span>
        )}
        {error && (
          <span role="alert" className="text-xs font-normal text-brk-danger">
            {error}
          </span>
        )}
        {quoteMissing && (
          <MissingFieldsNotice
            missing={quoteMissing}
            onCompleteProfile={onCompleteProfile}
          />
        )}

        {questions !== null && !quoteMissing && (
          <QuestionForm questions={questions} onSubmit={submitAnswers} />
        )}
      </div>
    </th>
  );
}

// The firm-quote gate (BRK-S057): the insured profile is missing account fields
// this carrier needs to bind, so the request is routed through profile
// completion rather than fired into a 400.
function ProfileGate({
  missing,
  onCompleteProfile,
}: {
  missing: MissingField[];
  onCompleteProfile?: () => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={onCompleteProfile}
        className="self-start rounded-md bg-brk-accent px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brk-accent-strong"
      >
        Complete the insured profile to quote
      </button>
      <span className="text-xs font-normal text-brk-dim">
        A firm quote needs: {missing.map((f) => f.label).join(", ")}.
      </span>
    </div>
  );
}

// A structured quote-readiness 400 rendered as a checklist with a route back to
// the profile form (BRK-S057) — mirrors AccountForm, never a raw string.
function MissingFieldsNotice({
  missing,
  onCompleteProfile,
}: {
  missing: MissingField[];
  onCompleteProfile?: () => void;
}) {
  return (
    <div
      role="status"
      className="flex flex-col gap-1 text-xs font-normal text-brk-dim"
    >
      <span>A few more insured details are needed to quote:</span>
      <ul className="list-inside list-disc">
        {missing.map((f) => (
          <li key={f.key}>{f.label}</li>
        ))}
      </ul>
      {onCompleteProfile && (
        <button
          type="button"
          onClick={onCompleteProfile}
          className="self-start text-brk-accent hover:underline"
        >
          Complete the insured profile
        </button>
      )}
    </div>
  );
}

function KindBadge({ kind }: { kind: BrokerQuote["kind"] }) {
  const label = kind === "quote" ? "Quote" : "Indication";
  return (
    <span className="rounded-full border border-brk-border bg-brk-bg px-2 py-0.5 text-xs font-medium text-brk-dim">
      {label}
    </span>
  );
}
